import { inject, Injectable } from '@angular/core';
import { APIService } from './api.service';
import { environment } from 'src/environments/environment';
import { Row, Table } from 'src/app/models/ui/table';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { WartungDB } from '../_offline/wartungDB';
import {
  ItemsMode,
  WartungsBaumNode,
  WartungsItem,
  WartungsMaskeItem,
  WartungsPDFEinstellung,
  WartungsPlanNode,
  WartungsZuordnung,
  WartungsZählerZuordnung,
} from 'src/app/content/_components/_overlays/wartungsverwaltung/wartung.types';
import { WartungsZuordnungModel } from 'src/app/content/_components/_overlays/wartungsverwaltung/wartungsobjektzuordnung/wartungsobjektzuordnung.component';
import {
  FileForPreview,
  FileAPIModel,
} from 'src/app/content/_components/_shared/preview-thumbnails/preview-thumbnail.type';
import {
  WartungProtokollAPI,
  ProtokollModel,
  ProtokollIDs,
  MultiProtokollModel,
} from 'src/app/content/Wartung/wartungdialog/wartung-protokoll.types';
import { MRproResponseInfo, ID_DBStatus, MRproResponse, ID, DBStatus, Media } from 'src/app/types/api';

const ROUTE = `${environment.apiUrl}/wartung/`;

@Injectable({
  providedIn: 'root',
})
export class WartungApiService {
  private readonly api = inject(APIService);
  private readonly http = inject(HttpClient);
  private readonly wartungDB = inject(WartungDB);
  private readonly options = this.api.AUTH_HEADER;
  //#region Verwaltung
  //#region Shared Settings
  public getWartungsItem<T = Row>(mode: ItemsMode, otypid: number) {
    return this.http.get<Table<T>>(ROUTE + `settings/${mode}/${otypid}`, this.options);
  }

  public setWartungsItem(mode: ItemsMode, otypid: number, sendObj: WartungsItem) {
    return this.http.post<MRproResponseInfo<ID_DBStatus>>(ROUTE + `settings/${mode}/${otypid}`, sendObj, this.options);
  }

  public deleteWartungsItem(mode: ItemsMode, id: number) {
    return this.http.delete<MRproResponse>(ROUTE + `settings/${mode}/${id}`, this.options);
  }
  //#endregion
  //#region Wartungsbaum
  public getWartungsBaum(otypid: number) {
    return this.http.get<WartungsBaumNode[]>(ROUTE + `baum/${otypid}`, this.options);
  }

  public setWartungsBaumNode(otypid: number, sendObj: WartungsBaumNode) {
    return this.api.isRep ?
        from(this.wartungDB.SetWartungsBaumNode(otypid, sendObj))
      : this.http.post<MRproResponseInfo<ID>>(ROUTE + `baum/${otypid}`, sendObj, this.options);
  }
  //#endregion
  //#region Masken
  public getWartungsMaskeList(maskeId: number) {
    return this.http.get<WartungsMaskeItem[]>(ROUTE + `masken/${maskeId}`, this.options);
  }

  public setWartungsMaskeList(
    id: number,
    otypid: number,
    sendObj: { arten: WartungsMaskeItem[] }
  ): Observable<MRproResponseInfo<{ IDs: number[] }>>;
  public setWartungsMaskeList(
    id: number,
    otypid: number,
    sendObj: { arten: WartungsMaskeItem[]; edit: true }
  ): Observable<MRproResponse>;
  public setWartungsMaskeList(id: number, otypid: number, sendObj: { arten: WartungsMaskeItem[]; edit?: boolean }) {
    return this.http.post<MRproResponseInfo<{ IDs?: number[] }>>(
      ROUTE + `masken/${id}/${otypid}`,
      sendObj,
      this.options
    );
  }

  public deleteWartungsMaske(id: number) {
    return this.http.delete<MRproResponse>(ROUTE + `masken/${id}`, this.options);
  }
  //#endregion
  //#region Objektzuordnung
  public getWartungsObjekte(otypid: number) {
    return this.http.get<Table>(ROUTE + `objekte/${otypid}`, this.options);
  }

  public getWartungsZuordnungen(ostammid: number) {
    return this.http.get<Table<WartungsZuordnung>>(ROUTE + `objekte-masken/${ostammid}`, this.options);
  }

  public setWartungsZuordnungen(sendObj: WartungsZuordnungModel) {
    return this.http.post<MRproResponseInfo<ID_DBStatus>>(ROUTE + 'objekte-masken', sendObj, this.options);
  }
  //#endregion
  //#region Zähler
  public getZaehlerZuordnungen(otypid: number) {
    return this.http.get<Table<WartungsZählerZuordnung>>(ROUTE + `masken-zaehler/${otypid}`, this.options);
  }

  public setZaehlerZuordnungen(szaehnid: number | null, sendObj: number[]) {
    return this.http.post<MRproResponse>(ROUTE + `masken-zaehler/${szaehnid}`, sendObj, this.options);
  }
  //#endregion
  //#region PDF
  public getWartungPDFSettings(): Observable<Table<WartungsPDFEinstellung>>;
  public getWartungPDFSettings() {
    return this.http.get(ROUTE + `pdf/settings`, this.options);
  }
  public setWartungPDFSettings(body: any) {
    return this.http.post(ROUTE + `pdf/settings`, body, this.options);
  }
  //#endregion
  //#endregion
  //#region Wartungsplan
  public getWartungsPlan(type: 'list', userId?: number): Observable<WartungsPlanNode[]>;
  public getWartungsPlan(type: 'table', userId?: number): Observable<Table<WartungsPlanNode>>;
  public getWartungsPlan(type: 'list' | 'table', userId = 0) {
    return this.api.isRep ?
        from(this.wartungDB.GetWartungenInPlan(type))
      : this.http.get(ROUTE + `plan/${type}/${userId}`, this.options);
  }
  //#endregion
  //#region Protokoll
  public getWartungsBemerkungenList() {
    return this.api.isRep ?
        from(this.wartungDB.GetWartungenBemerkungen())
      : this.http.get<string[]>(ROUTE + `bemerkungen`, this.options);
  }

  public getWartungNachMaske(mID: number | null, wID: number, readonly: boolean) {
    return this.api.isRep ?
        from(this.wartungDB.GetWartungFromMaske(mID, wID, readonly))
      : this.http.get<WartungProtokollAPI[]>(ROUTE + `${mID}/${wID}/${readonly}`, this.options);
  }

  public setWartung(sendObj: ProtokollModel) {
    return this.api.isRep ?
        from(this.wartungDB.SetWartung(sendObj))
      : this.http.post<MRproResponseInfo<ProtokollIDs>>(ROUTE + 'protokoll', sendObj, this.options);
  }

  public setMultiWartung(sendObj: MultiProtokollModel) {
    return this.api.isRep ?
        from(this.wartungDB.SetMultiWartung(sendObj))
      : this.http.post<MRproResponse>(ROUTE + 'protokoll/multi', sendObj, this.options);
  }
  public getWartungsArbeitID(arbeitName: string, otypid: number) {
    return this.api.isRep ?
        from(this.wartungDB.GetArbeitID(arbeitName, otypid))
      : this.http.get<number>(ROUTE + `findOrCreateArbeitID/${arbeitName}/${otypid}`, this.options);
  }
  //#endregion
  //#region Media
  public getWartungMediaByID(wID: number) {
    return this.api.isRep ?
        from(this.wartungDB.GetWartungMediaByID(wID))
      : this.http.get<FileForPreview[]>(ROUTE + `media/${wID}`, this.options);
  }

  public setWartungsImage(isPassbild: boolean, wID: number, id: number, sendObj: FileAPIModel) {
    return this.api.isRep ?
        from(this.wartungDB.SetWartungsImage(isPassbild, wID, id, sendObj))
      : this.http.post<MRproResponseInfo<{ mediaStr: string }>>(
          ROUTE + `media/${isPassbild}/${wID}/${id}`,
          sendObj,
          this.options
        );
  }

  public getWartungMediaAllByNames(names: string[]) {
    return this.api.isRep ?
        from(this.wartungDB.GetWartungMediaAllByNames(names))
      : this.http.post<MRproResponseInfo<Media>>(ROUTE + 'media', names, this.options);
  }

  public getWartungMediaByName(filename: string) {
    return this.api.isRep ?
        from(this.wartungDB.GetWartungMediaByName(filename))
      : this.http.get<string>(ROUTE + `media/${encodeURIComponent(filename)}`, this.options);
  }
  //#endregion
}
